/**
 * Edit-mode date row for a manual/booked slot, rendered as ONE custom
 * FormBuilder field so we control the layout with flex (the FormBuilder
 * grid can't do "full-width when alone, side-by-side when both"):
 *
 *   - Same-day slot  → Start date alone, full width.
 *   - Multi-day slot → Start date | End date, side by side.
 *
 * Start/End dates live in the form as `startDate` / `endDate` (single
 * source of truth — read via `watch`, written via `setValue`), so they
 * round-trip on edit without the duplicate-field binding bug that two
 * layout variants caused. Each change is pushed through `onFieldChange`
 * so `useFormCalendarSync` recomputes the slot exactly as it does for
 * the native date fields.
 */
import { Box, Flex, Text } from '@chakra-ui/react';
import { DatePicker } from '@kula-ai/components';
import moment from 'moment-timezone';

interface DateRangeFieldsFormMethods {
  watch: (_name: string) => unknown;
  setValue: (_name: string, _value: unknown) => void;
  getValues: () => Record<string, unknown>;
}

interface DateRangeFieldsProps {
  formMethods: DateRangeFieldsFormMethods;
  /** Mirrors the wizard's `onFieldChange` so the calendar/slot sync runs. */
  onFieldChange?: (
    _name: string,
    _value: unknown,
    _allValues: Record<string, unknown>
  ) => void;
  startMinDate: Date;
  endMinDate: Date;
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="sm" fontWeight="semibold" mb="1.5" color="secondary-900">
    {children}{' '}
    <Text as="span" color="error-200">
      *
    </Text>
  </Text>
);

export const DateRangeFields = ({
  formMethods,
  onFieldChange,
  startMinDate,
  endMinDate,
}: DateRangeFieldsProps) => {
  const startDate = (formMethods.watch('startDate') as Date | null) ?? null;
  const endDate = (formMethods.watch('endDate') as Date | null) ?? null;

  // Multi-day = start and end land on different calendar days. End date
  // only renders then (legacy `!isSameDay && endDate`); same-day keeps
  // Start date alone at full width.
  const isMultiDay =
    !!startDate &&
    !!endDate &&
    !moment(startDate).isSame(moment(endDate), 'day');

  const handleStartChange = (date: Date) => {
    formMethods.setValue('startDate', date);
    onFieldChange?.('startDate', date, formMethods.getValues());
  };

  const handleEndChange = (date: Date) => {
    formMethods.setValue('endDate', date);
    onFieldChange?.('endDate', date, formMethods.getValues());
  };

  return (
    <Flex gap="3" w="100%">
      <Box flex="1" minW={0}>
        <FieldLabel>Start date</FieldLabel>
        <DatePicker
          mode="single"
          value={startDate}
          onChange={handleStartChange}
          triggerWidth="100%"
          placeholder="Select date"
          calendarProps={{ minDate: startMinDate }}
        />
      </Box>
      {isMultiDay && (
        <Box flex="1" minW={0}>
          <FieldLabel>End date</FieldLabel>
          <DatePicker
            mode="single"
            value={endDate}
            onChange={handleEndChange}
            triggerWidth="100%"
            placeholder="Select date"
            calendarProps={{ minDate: endMinDate }}
          />
        </Box>
      )}
    </Flex>
  );
};
